// src/components/FinanceShell.js
// Wraps the whole app. On a role-center page (Finance, HR, Payroll, Academics)
// it shows that module's collapsible side menu — permanent (pinned open, content
// shifts right) on desktop, and a slide-out drawer with an edge handle on mobile —
// so users can jump between screens without going home. On other pages it renders
// the content untouched. Which menu shows is resolved from the current path.
import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItemButton, ListItemText, ListItemIcon, Collapse, Box,
  Typography, Divider, IconButton, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ExpandLess, ExpandMore, Menu as MenuIcon, Close, AccountBalance, Home,
  AccountBalanceWallet, People, Business, HourglassBottom, Inventory2, Tune,
  School, Calculate, EventNote, WorkspacePremium, Gavel, BeachAccess,
  Apps, ChevronRight, Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { resolveModuleNav } from '../config/moduleNav';
import GlobalSearch from './GlobalSearch';
import AppUpdateNotice from './AppUpdateNotice';
import InstallPrompt from './InstallPrompt';

const DRAWER_W = 264;

// A nav item is active when the path matches its path. Items flagged `exact`
// (e.g. a module dashboard whose path is a prefix of its siblings) only match
// on an exact path; others also match nested sub-paths.
const matchesItem = (item, path) =>
  item.exact ? path === item.path : (path === item.path || path.startsWith(item.path + '/'));

const GROUP_ICON = {
  'General Ledger': AccountBalance,
  'Receivables': People,
  'Payables': Business,
  'Banks & Cash': AccountBalanceWallet,
  'Fixed Assets': HourglassBottom,
  'Stores & Inventory': Inventory2,
  'Setups': Tune,
  // HR / Payroll / Academics groups
  'Employees': People,
  'Leave': BeachAccess,
  'Performance': WorkspacePremium,
  'Disciplinary & Exit': Gavel,
  'Processing': Calculate,
  'Reports': EventNote,
  'Setup': Tune,
  'Overview': DashboardIcon,
  'Structure': School,
  'Assessment': EventNote,
  'Fees & Charges': Business,
  'Events': EventNote,
};

export default function FinanceShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const path = location.pathname;
  const userRole = (JSON.parse(localStorage.getItem('user') || 'null') || {}).role;
  const mod = resolveModuleNav(path, userRole);
  const groups = useMemo(() => mod?.groups || [], [mod]);
  const pinned = !!mod && desktop;

  const [mobileOpen, setMobileOpen] = useState(false);

  const activeGroup = useMemo(() => {
    if (!mod) return null;
    const g = groups.find(grp => grp.items.some(it => matchesItem(it, path)));
    return g?.title || groups[0]?.title;
  }, [path, mod, groups]);

  const [expanded, setExpanded] = useState({});
  const isExpanded = (title) => (title in expanded ? expanded[title] : title === activeGroup);
  const toggleGroup = (title) => setExpanded(e => ({ ...e, [title]: !isExpanded(title) }));

  const go = (to) => { setMobileOpen(false); navigate(to); };
  const isActiveItem = (item) => matchesItem(item, path);

  const ModIcon  = mod?.Icon || AccountBalance;
  const modTitle = mod?.title || 'Menu';
  const modHome  = mod?.home || '/dashboard';
  const modColor = mod?.color || '#0a1931';

  const drawerContent = (
    <Box sx={{ width: DRAWER_W, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }} role="navigation">
      {/* Header */}
      <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: modColor, color: 'white' }}>
        <ModIcon />
        <Typography fontWeight={800} flex={1} letterSpacing={0.3}>{modTitle}</Typography>
        <Tooltip title={`${modTitle} Home`}>
          <IconButton size="small" onClick={() => go(modHome)} sx={{ color: 'white' }}><Home fontSize="small" /></IconButton>
        </Tooltip>
        {!desktop && (
          <IconButton size="small" onClick={() => setMobileOpen(false)} sx={{ color: 'white' }}><Close fontSize="small" /></IconButton>
        )}
      </Box>
      <Divider />

      {/* Groups */}
      <List sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {groups.map(group => {
          const Icon = GROUP_ICON[group.title] || AccountBalance;
          const openG = isExpanded(group.title);
          return (
            <Box key={group.title}>
              <ListItemButton onClick={() => toggleGroup(group.title)} sx={{ py: 0.75, borderRadius: 1.5, mx: 0.75 }}>
                <ListItemIcon sx={{ minWidth: 34, color: group.color }}><Icon fontSize="small" /></ListItemIcon>
                <ListItemText primary={group.title}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: 12.5, textTransform: 'uppercase', letterSpacing: 0.4 }} />
                {openG ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
              </ListItemButton>
              <Collapse in={openG} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {group.items.map(item => (
                    <ListItemButton key={item.path} onClick={() => go(item.path)}
                      selected={isActiveItem(item)}
                      sx={{
                        pl: 5.5, py: 0.5, mx: 0.75, borderRadius: 1.5,
                        '&.Mui-selected': {
                          bgcolor: 'primary.main', color: 'white',
                          '&:hover': { bgcolor: 'primary.dark' },
                        },
                      }}>
                      <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      {/* Class teachers have no access to the main dashboard — their home is the
          Class Teacher Centre — so don't offer the jump-to-dashboard button. */}
      {userRole !== 'classTeacher' && (
        <>
          <Divider />
          <Box sx={{ p: 1.25 }}>
            <ListItemButton onClick={() => go('/dashboard')}
              sx={{
                borderRadius: 2, py: 1.1, px: 1.5, gap: 1,
                bgcolor: modColor, color: 'white', boxShadow: 2,
                transition: 'transform .12s, box-shadow .12s, filter .12s',
                '&:hover': { bgcolor: modColor, filter: 'brightness(1.12)', boxShadow: 4, transform: 'translateY(-1px)' },
              }}>
              <ListItemIcon sx={{ minWidth: 30, color: 'inherit' }}><Apps fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Main Dashboard"
                secondary="All role centers"
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: 800, color: 'white' }}
                secondaryTypographyProps={{ fontSize: 10.5, sx: { color: 'rgba(255,255,255,0.75)' } }}
              />
              <ChevronRight fontSize="small" sx={{ opacity: 0.85 }} />
            </ListItemButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Permanent sidebar on desktop finance pages */}
      {pinned && (
        <Drawer variant="permanent"
          sx={{ width: DRAWER_W, flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_W, boxSizing: 'border-box', borderRight: '1px solid #e6e8eb' } }}>
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile: edge handle + slide-out drawer on role-center pages */}
      {!!mod && !desktop && (
        <>
          {!mobileOpen && (
            <Tooltip title={`${modTitle} menu`} placement="right">
              <IconButton onClick={() => setMobileOpen(true)}
                sx={{ position: 'fixed', top: 88, left: 0, zIndex: (t) => t.zIndex.drawer - 1,
                  bgcolor: 'primary.main', color: 'white', borderRadius: '0 8px 8px 0', boxShadow: 3,
                  '&:hover': { bgcolor: 'primary.dark' } }}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }}>
            {drawerContent}
          </Drawer>
        </>
      )}

      {/* Page content (shifts right beside the pinned sidebar) */}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
        {children}
      </Box>

      {/* Global "Tell Me" search — available on every route (Ctrl/⌘+K) */}
      <GlobalSearch />

      {/* Watches for a newly deployed build and invites a reload */}
      <AppUpdateNotice />

      {/* Post-login "Install EduMIS" popup (web only; hidden when installed) */}
      <InstallPrompt />
    </Box>
  );
}
